import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import * as Colors from 'material-ui/styles/colors';
import _ from 'lodash';
import SmoothCollapse from 'shared/vendor/react-smooth-collapse-master/src/index';
import Paper from 'material-ui/Paper';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import IconButton from 'material-ui/IconButton';
import LinearProgress from 'material-ui/LinearProgress';
import Sticky from '../Sticky/Sticky';
import TooltipIcon from '@material-ui/core/TooltipIcon';
import TooltipWrapper from 'shared/components/TooltipWrapper';
import Fade from '@material-ui/core/Fade/Fade';
import {stringToDashed} from 'cl-automation-utils';
export const styles = {};
export default class CollapsingWrapper extends PureComponent {
  state = {
    aboveView: false,
    open: this.props.defaultOpen,
  };

  componentDidMount() {
    const {onDidMount} = this.props;
    if (_.isFunction(onDidMount)) {
      onDidMount();
    }
  }

  handleChangeEnd = () => {
    const {onRest, onChangeEnd} = this.props;
    if (_.isFunction(onChangeEnd)) {
      onChangeEnd();
    }
    if (_.isFunction(onRest)) {
      onRest();
    }
  };

  handleUncontrolledToggle = () => {
    const {open} = this.state;
    this.setState({open: !open});
  };

  render() {
    const {
      errorText,
      style,
      first,
      stickyId,
      alwaysRenderChildren,
      stickyTitle,
      tooltip,
      dataReady,
      solidColor,
      leftRightOpenMargin,
      title,
      children,
      transition,
      openBackgroundColor,
      closedBackgroundColor,
      disabledBackgroundColor,
      openBoxShadow,
      closedBoxShadow,
      collapsedHeight,
      overflowVisible,
      onRest,
      onDidMount,
      onChangeEnd,
      disabled,
      disabledTooltip,
      defaultOpen,
      ...other
    } = this.props;
    let {open, onToggleOpen, id} = this.props;
    if (_.isUndefined(open) || _.isUndefined(onToggleOpen)) {
      // Uncontrolled collapsing wrapper
      open = this.state.open;
      onToggleOpen = this.handleUncontrolledToggle;
    }
    const openRequested = open;
    if (!dataReady || disabled) {
      open = false;
    }
    const backgroundColor = open ? openBackgroundColor : closedBackgroundColor;
    const boxShadow = open ? openBoxShadow : closedBoxShadow;
    id = id ? id : stringToDashed(_.get(title, 'props.children[0]', title)).toLowerCase();
    const rootStyle = {
      margin: open ? `${first ? 1 : 20}px ${leftRightOpenMargin}px 20px ${leftRightOpenMargin}px` : '1px 0px 0px 0px',
      transition,
      backgroundColor: solidColor ? backgroundColor : Colors.white,
      ...style,
    };
    if (boxShadow) {
      rootStyle.boxShadow = boxShadow;
    }
    const TitleElement = stickyTitle ? Sticky : 'div';
    const content = (
      <Paper id={`cla-section-${id}`} zDepth={open ? 2 : 1} style={rootStyle} {..._.omit(other, 'onToggleOpen')}>
        <TitleElement
          onClick={disabled ? null : onToggleOpen}
          style={{
            backgroundColor: disabled ? disabledBackgroundColor || Colors.grey400 : backgroundColor,
            padding: '0px 0px 0px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition,
            position: 'relative',
            cursor: disabled ? undefined : 'pointer',
          }}
          disabled={stickyTitle && !open}
          id={stickyId}
        >
          <div id={`cla-section-text-${id}`} style={{display: 'flex'}}>
            {title}
            {!disabled && tooltip && (
              <TooltipIcon
                id={`cla-section-tooltip-${id}`}
                variant={'inline'}
                style={{marginLeft: 5, paddingRight: 0}}
                title={tooltip}
              />
            )}
            <Fade in={!!errorText} style={{marginLeft: 15, color: Colors.red700}}>
              <div>{errorText}</div>
            </Fade>
          </div>
          <IconButton
            id={open ? `cla-section-collapse-${id}` : `cla-section-expand-${id}`}
            iconStyle={{transform: open ? 'rotate(180deg)' : null}}
            disabled={disabled}
          >
            <KeyboardArrowDownIcon id={open ? `cla-section-icon-collapse-${id}` : `cla-section-icon-expand-${id}`} />
          </IconButton>
          {openRequested && !dataReady && (
            <LinearProgress
              style={{
                backgroundColor: Colors.transparent,
                height: 2,
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
              }}
              color={Colors.lightGreen500}
            />
          )}
        </TitleElement>
        <SmoothCollapse
          alwaysRenderChildren={alwaysRenderChildren}
          overflowVisible={overflowVisible}
          expanded={open}
          heightTransition={transition}
          onChangeEnd={this.handleChangeEnd}
          collapsedHeight={collapsedHeight}
        >
          {React.Children.map(children, child =>
            React.cloneElement(child, {open, style: {...child.props.style, transition}})
          )}
        </SmoothCollapse>
      </Paper>
    );
    if (disabled !== undefined) {
      return <TooltipWrapper tooltip={disabled ? disabledTooltip : ''}>{content}</TooltipWrapper>;
    }
    return content;
  }
}

CollapsingWrapper.propTypes = {
  initialValue: PropTypes.string,
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onToggleOpen: PropTypes.func,
  onChange: PropTypes.func,
  onRest: PropTypes.func,
  openBackgroundColor: PropTypes.string.isRequired,
  closedBackgroundColor: PropTypes.string.isRequired,
  transition: PropTypes.string.isRequired,
  openBoxShadow: PropTypes.string, // leave empty Paper zDepth 2
  closedBoxShadow: PropTypes.string,
  collapsedHeight: PropTypes.string,
  overflowVisible: PropTypes.bool,
  leftRightOpenMargin: PropTypes.number,
  solidColor: PropTypes.bool,
  dataReady: PropTypes.bool,
  tooltip: PropTypes.string,
  stickyTitle: PropTypes.bool,
  alwaysRenderChildren: PropTypes.bool,
  stickyId: PropTypes.string,
  first: PropTypes.bool,
  errorText: PropTypes.string,
};

const DEFAULT_BOX_SHADOW = '0 -1px 0 #e0e0e0, 0 0 2px rgba(0,0,0,.12), 0 2px 4px rgba(0,0,0,.24)';
CollapsingWrapper.defaultTransitionDuration = 300;

CollapsingWrapper.defaultProps = {
  defaultOpen: false,
  openBackgroundColor: Colors.grey50,
  closedBackgroundColor: Colors.white,
  transition: `${CollapsingWrapper.defaultTransitionDuration}ms cubic-bezier(0.68,-0.01, 0.49, 0.91)`,
  closedBoxShadow: DEFAULT_BOX_SHADOW,
  leftRightOpenMargin: -16,
  dataReady: true,
};
